import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import { useSetState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { BaseLineT2atip } from '@/app/components/icons/BaseLineT2atip';
import { ProductLineAudio } from '@/app/components/icons/ProductLineAudio';
import { ProductLineTimestamp } from '@/app/components/icons/ProductLineTimestamp';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { TipsCarousel } from '@/app/components/shared/TipsCarousel';
import { Button } from '@/app/components/ui/button';
import { useToast } from '@/app/components/ui/use-toast';
import { AUDIO_DURATION, SITE } from '@/app/constants';
import type { SegmentType } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { SAMPLES, T2A_LIMIT, T2A_TIPS_INTERVAL, T2A_TIPS_TEXT } from '../../constant';
import type { TimeTagElement } from '../../types';
import { ConfirmDelete } from './ConfirmDelete';
import { SuggestionHint, TabSuggestionExtension } from './TabSuggestion';
import { TimeTag } from './TimeTag';
import { TimeTagPopover } from './TimeTagPopover';
import { extractContentFromEditor, generateEditorContent, isEmoji } from './utils';

type State = {
  activeTimeTag: TimeTagElement | null;
  isTipsVisible: boolean;
};

interface TimestampTextAreaProps {
  prompts?: SegmentType[];
  onChange?: (content: SegmentType[], totalDuration?: number) => void;
}

export interface TimestampTextAreaRef {
  handleClickTimestamp: (timeTag?: Partial<SegmentType>) => void;
}

export const TimestampTextArea = forwardRef<TimestampTextAreaRef, TimestampTextAreaProps>(
  ({ prompts = [], onChange }, ref) => {
    const t = useTranslations();
    const { toast } = useToast();
    const locale = useLocale();

    const [state, setState] = useSetState<State>({
      activeTimeTag: null,
      isTipsVisible: true,
    });

    const isEditingRef = useRef(false);
    const isInternalUpdate = useRef(false);

    const handleEditorUpdate = ({ editor }: { editor: Editor }) => {
      if (!onChange) return;
      isInternalUpdate.current = true;

      // 检查内容中是否包含 emoji
      let hasEmoji = false;
      editor.state.doc.descendants((node) => {
        if (node.isText && isEmoji(node.text || '')) {
          hasEmoji = true;
          return false;
        }
        return true;
      });

      if (hasEmoji) {
        editor.commands.undo();
        toast({
          type: 'warning',
          message: t('text2audio.timestampTextArea.invalidCharacter'),
        });
        return;
      }

      // 检查当前激活的时间标签是否还存在
      if (state.activeTimeTag) {
        let timeTagExists = false;
        editor.state.doc.nodesBetween(0, editor.state.doc.content.size, (node, pos) => {
          if (pos === state.activeTimeTag?.pos && node.type.name === 'timeTag') {
            timeTagExists = true;
            return false;
          }
          return true;
        });

        // 如果时间标签不存在了，关闭 popover
        if (!timeTagExists) {
          setState({ activeTimeTag: null });
        }
      }

      const extractedContent = extractContentFromEditor(editor);
      onChange(extractedContent);
    };

    const editor = useEditor({
      immediatelyRender: false,
      extensions: [
        Document,
        Text,
        Paragraph.configure({
          HTMLAttributes: {
            class: 'mb-4 leading-[24px] whitespace-normal',
          },
        }),
        History,
        TimeTag,
        SuggestionHint,
        TabSuggestionExtension,
      ],
      content: generateEditorContent(prompts),
      editorProps: {
        attributes: {
          class: 'max-h-[300px] overflow-y-auto h-full',
        },
        handleDOMEvents: {
          click: (view, event) => {
            if (isEditingRef.current) return false;

            const target = event.target as HTMLElement;
            if (target.closest('.time-tag-popover')) return false;

            if (target.hasAttribute('data-time-tag')) {
              const start = Number(target.getAttribute('start'));
              const end = Number(target.getAttribute('end'));
              const pos = view.posAtDOM(target, 0);
              const rect = target.getBoundingClientRect();

              // 点击已激活的标签关闭弹窗
              if (state.activeTimeTag && state.activeTimeTag.pos === pos) {
                setState({ activeTimeTag: null });
                return true;
              }
              setState({
                activeTimeTag: {
                  start,
                  end,
                  pos,
                  element: target,
                  rect,
                },
              });
              return true;
            }
            return false;
          },
          compositionend: (_, event): boolean => {
            if (!editor) return false;

            if (event.data === '\n' || event.data === '\r') {
              setTimeout(handleClickTimestamp, 0);
              return true;
            }

            // 检查输入的文字是否符合限制
            if (!checkTextLimits(event.data)) {
              editor.commands.undo();
              return true;
            }

            return false;
          },
        },
        handleKeyDown: (_, event) => {
          // 处理回车事件
          if (event.key === 'Enter') {
            setTimeout(handleClickTimestamp, 0);
            return false;
          }

          // 过滤不允许的字符输入
          const disallowedCharsRegex = /[@#¥（）+\-/$%^&*{}[\]<>~`'"\\|=]/;
          if (disallowedCharsRegex.test(event.key)) {
            event.preventDefault();
            toast({
              type: 'warning',
              message: t('text2audio.timestampTextArea.invalidCharacter'),
            });
            return true;
          }

          // 检查字符数量（对于单字符输入）
          if (event?.key?.length === 1 && !event.ctrlKey && !event.metaKey && editor) {
            if (!checkTextLimits(event.key)) {
              event.preventDefault();
              return true;
            }
          }

          return false;
        },
        handlePaste(_, event) {
          const text = event.clipboardData?.getData('text/plain');
          if (text) {
            event.preventDefault();

            // 检查是否包含emoji
            if (isEmoji(text)) {
              toast({
                type: 'warning',
                message: t('text2audio.timestampTextArea.invalidCharacter'),
              });
              return true;
            }

            // 检查是否包含不允许的特殊字符
            const disallowedCharsRegex = /[@#¥（）+\-/$%^&*{}[\]<>~`'"\\|=]/;
            if (disallowedCharsRegex.test(text)) {
              toast({
                type: 'warning',
                message: t('text2audio.timestampTextArea.invalidCharacter'),
              });
              return true;
            }

            // 检查文本长度限制
            if (!checkTextLimits(text)) {
              return true;
            }

            editor?.commands.insertContent(text);
          }
          return true;
        },
      },
      onUpdate: handleEditorUpdate,
      onFocus: ({ editor }) => {
        if (editor.isEmpty && !isEditingRef.current) {
          setTimeout(handleClickTimestamp, 0);
        }
      },
    });

    const checkTextLimits = (text: string | undefined): boolean => {
      if (!text || !editor) return true;

      // 获取当前选中段落的文本内容
      const { $from } = editor.state.selection;
      const currentNode = $from.parent;
      const currentText = currentNode.textContent;

      // 计算当前所有段落的总字符数
      let totalChars = 0;
      editor.state.doc.descendants((node) => {
        if (node.type.name === 'paragraph') {
          totalChars += node.textContent.length;
        }
        return true;
      });

      // 计算添加新文本后的当前段落字符数
      const newSegmentLength = currentText.length + text.length;
      // 计算添加新文本后的总字符数
      const newTotalChars = totalChars + text.length;

      // 检查当前段落是否超出单段限制
      if (newSegmentLength > T2A_LIMIT.SEGMENT_TEXT_LENGTH) {
        toast({
          type: 'warning',
          message: t('text2audio.timestampTextArea.segmentLimitExceeded', { limit: T2A_LIMIT.SEGMENT_TEXT_LENGTH }),
        });
        return false;
      }

      // 检查是否超出总字数限制
      if (newTotalChars > T2A_LIMIT.TOTAL_TEXT_COUNT) {
        toast({
          type: 'warning',
          message: t('text2audio.timestampTextArea.totalLimitExceeded', { limit: T2A_LIMIT.TOTAL_TEXT_COUNT }),
        });
        return false;
      }

      return true;
    };

    useEffect(() => {
      if (!editor?.isEditable) return;
      // 防止在处理中触发更多更新
      if (isInternalUpdate.current) {
        isInternalUpdate.current = false;
        return;
      }
      editor.commands.setContent(generateEditorContent(prompts));
    }, [editor, prompts]);

    const handleClickTimestamp = (timeTag?: Partial<SegmentType>) => {
      if (!editor) return;

      isEditingRef.current = true;

      editor
        .chain()
        .focus()
        .splitBlock()
        .setTimeTag({ ...timeTag, color: timeTag?.color })
        .insertContent(timeTag?.text ?? '')
        .run();

      if (onChange) {
        setTimeout(() => {
          const extractedContent = extractContentFromEditor(editor);
          onChange(extractedContent);
          isEditingRef.current = false;
        }, 10);
      } else {
        setTimeout(() => {
          isEditingRef.current = false;
        }, 10);
      }
    };

    useImperativeHandle(ref, () => ({
      handleClickTimestamp,
    }));

    const handleTimeTagChange = (newStart: number, newEnd: number) => {
      if (!editor || !state.activeTimeTag) return;
      isEditingRef.current = true;

      // 获取当前节点的原始属性
      const node = editor.state.doc.nodeAt(state.activeTimeTag.pos);
      const currentAttrs = node?.attrs || {};

      editor
        .chain()
        .command(({ tr, dispatch }) => {
          if (dispatch && state.activeTimeTag) {
            tr.setNodeMarkup(state.activeTimeTag.pos, undefined, {
              ...currentAttrs,
              start: newStart,
              end: newEnd,
            });
          }
          return true;
        })
        .run();

      if (onChange) {
        onChange(extractContentFromEditor(editor));
      }

      setState({
        activeTimeTag: {
          ...state.activeTimeTag,
          start: newStart,
          end: newEnd,
        },
      });

      setTimeout(() => {
        isEditingRef.current = false;
      }, 100);
    };

    const handleClosePopover = () => {
      if (!isEditingRef.current) {
        setState({ activeTimeTag: null });
      }
    };

    const handleClearAll = () => {
      if (!editor) return;
      editor.commands.clearContent();
      onChange?.([], AUDIO_DURATION);
      setState({ activeTimeTag: null });
    };

    return (
      <div className="relative flex min-h-[300px] w-full flex-col rounded-12 bg-black/[36%] p-3">
        {!prompts.length && (
          <div className="pointer-events-none absolute right-3 left-3 select-none font-normal text-sm text-system-white36">
            {t.rich('text2audio.timestampTextArea.placeholder')}
          </div>
        )}

        <EditorContent editor={editor} className="flex-1" />

        {/* 时间标签编辑弹窗 */}
        {state.activeTimeTag && (
          <TimeTagPopover timeTag={state.activeTimeTag} onChange={handleTimeTagChange} onClose={handleClosePopover} />
        )}

        <div className={cn('mb-3 flex w-full justify-end px-2 pb-3 shadow-white10', !prompts.length && 'hidden')}>
          <ConfirmDelete onConfirm={handleClearAll} />
        </div>

        <TipsCarousel
          className="mb-3"
          isTipsVisible={state.isTipsVisible}
          tipsData={T2A_TIPS_TEXT[SITE][locale] || []}
          intervalDuration={T2A_TIPS_INTERVAL * 1000}
        />

        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-1 items-center gap-2 overflow-hidden">
            <div className="hide-scrollbar flex flex-1 items-center gap-2 overflow-x-auto">
              {SAMPLES[SITE].map((sample, index) => (
                <Button
                  key={`sample-${sample.id}`}
                  variant="outline"
                  className={cn('rounded-[100px] border-none bg-[#1D2133] px-4 font-normal', index > 0 && 'md:hidden')}
                  size="sm"
                  tabIndex={-1}
                  onClick={() => {
                    reportEvent('try_samples_click', {
                      from_page: 'create/text2audio',
                      samples_index: index + 1,
                    });
                    if (editor && onChange) {
                      editor.commands.setContent(generateEditorContent(sample.segments));
                      onChange(sample.segments, sample.duration);
                    }
                  }}
                >
                  <ProductLineAudio className={cn('text-base', locale === 'zh' ? 'inline' : 'hidden')} />
                  <span className="ml-1">
                    {t('text2audio.timestampTextArea.sample')}
                    {index + 1}
                  </span>
                </Button>
              ))}
            </div>
            <Button
              className={cn('flex-shrink-0 rounded-[100px] border-none bg-[#1D2133] font-normal')}
              size="sm"
              onClick={() => setState({ isTipsVisible: !state.isTipsVisible })}
            >
              <BaseLineT2atip className="text-lg" isActive={state.isTipsVisible} />
            </Button>
          </div>

          <Button
            variant="outline"
            className="rounded-[100px] border-none font-normal"
            size="sm"
            onClick={() => {
              handleClickTimestamp();
              reportEvent('create_element_click', {
                element_name: 'button_time_stamp_prompt',
              });
            }}
          >
            <CustomTooltip content={t('text2audio.timestampTextArea.timestampTip')}>
              <span>
                <ProductLineTimestamp className="text-base" />
              </span>
            </CustomTooltip>
            <span className="ml-1">{t('text2audio.timestampTextArea.timestamp')}</span>
          </Button>
        </div>
      </div>
    );
  },
);
